<template>
  <div class="container">
    <bonus-title initial-tab-value="events" />
    <div class="t m-tx4">
      <span>{{ description }}</span>
      <a href="/rewards/settings" target="_blank" class="link ul">{{ rewardsStoreNoun }} settings</a>
      <span>.</span>
    </div>
    <div class="t m-tx2">This message will be sent to each recipient as a private DM!</div>
    <donut-table :headers="tableHeaders" :data="tableData" class="m-tx4">
      <!-- Title -->
      <template #event="{ entry }">
        <div class="t bold">{{ entry.title }}</div>
        <div class="t-sm gray m-t2">{{ entry.description }}</div>
      </template>
      <!-- Points -->
      <template #points="{ entry }">
        <div v-emoji="`:${pointsConfig.emoji}: ${entry.points.toLocaleString()}`" class="t-sm" />
      </template>
      <!-- Actions -->
      <template #actions="{ entry }">
        <div class="f">
          <lucide-icon name="Pencil" :stroke-width="2" class="purple c-p" @click="openActiveTemplateModal('configure', entry)" />
          <lucide-icon name="Trash" :stroke-width="2" class="purple c-p m-lx2" @click="openDeleteConfirmationModal(entry)" />
          <donut-button
            class="inv m-lx2"
            :show-upgrade-badge="state.bonusEventTemplates.sendBehavior.startsWith('upgrade_required')"
            @click="clickSend(entry)"
          >
            <span>Send</span>
            <lucide-icon name="SendHorizontal" :stroke-width="2" class="m-l" />
          </donut-button>
        </div>
      </template>
    </donut-table>
    <div v-if="tableData.length === 0" class="t gray m-tx2">There are no Bonus Events. Use the button below to create one.</div>
    <button class="m-tx4" @click="openActiveTemplateModal('configure')">+ Add Bonus Event</button>
  </div>
</template>

<script setup>
  import { computed, inject } from 'vue';
  import { useStore } from 'vuex';

  const $i18n = inject('$i18n');
  const { state, commit, dispatch } = useStore();

  const pointsNoun = $i18n.t('shoutouts.point', { count: 2 });
  const rewardsNoun = $i18n.t('shoutouts.reward', { count: 2 });
  const rewardsStoreNoun = $i18n.t('shoutouts.rewards_store');
  const { pointsConfig } = state.bonusEventTemplates;

  const description = `Celebrate special occasions by awarding ${pointsNoun}. If the ${rewardsStoreNoun} is enabled, recipients can `
    + `redeem their ${pointsNoun} for custom ${rewardsNoun} or gift cards. Donut will send them a direct message to congratulate them `
    + `and deliver their ${pointsNoun}. You can customize ${rewardsNoun} in the `;

  // Table
  const tableHeaders = [
    { label: 'Bonus Event', key: 'event', weight: 3 },
    { label: pointsNoun, key: 'points', weight: 3 },
    { key: 'actions', weight: 2 },
  ];
  const tableData = computed(() => state.bonusEventTemplates.eventTemplates);

  // Modals
  const openActiveTemplateModal = (modalType, eventTemplate) => {
    dispatch('bonusEventTemplates/openActiveTemplateModal', { modalType, eventTemplate });
  };
  const openDeleteConfirmationModal = (eventTemplate) => {
    commit('updateModal', {
      visible: true,
      subcomponent: 'bonus-event-templates-modals-delete',
      data: { eventTemplate },
    });
  };

  const openUpgradeModal = (trialAllowed) => {
    dispatch('openChannelProgramsPlanUpgradeModal', {
      trialAllowed,
      data: { featureTitleText: 'send Bonus Points', source: 'bonus-event-send', featureInfo: { programKey: 'shoutouts' } },
    });
  };

  const clickSend = (entry) => {
    // eslint-disable-next-line default-case
    switch (state.bonusEventTemplates.sendBehavior) {
      case 'allow_send':
        openActiveTemplateModal('send', entry);
        break;
      case 'upgrade_required_trial':
        openUpgradeModal(true);
        break;
      case 'upgrade_required_payment':
        openUpgradeModal(false);
        break;
      case 'contact_support':
        commit('updateModal', {
          visible: true,
          subcomponent: 'payment-modals-contact-to-get-started',
          data: { source: 'bonus-event-send' },
        });
        break;
    }
  };
</script>
